import React, { useState, useRef } from 'react';
import { Plus } from 'lucide-react';
import { ROLE_SEED } from '../constants';
import {
  BRAND_DARK, BRAND_SOFT, TEXT_PRIMARY, TEXT_TERTIARY,
  BG_CARD, BG_HOVER, BORDER, BORDER_LIGHT,
} from '../styles';

interface Props {
  value: string;
  onChange: (v: string) => void;
}

export default function RoleCombobox({ value, onChange }: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const [customRoles, setCustomRoles] = useState<string[]>([]);
  const [customMode, setCustomMode] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const allRoles = [...ROLE_SEED, ...customRoles];
  const typed = value.trim();
  const filtered = typed === ''
    ? allRoles
    : allRoles.filter((r) => r.toLowerCase().includes(typed.toLowerCase()));
  const exactMatch = allRoles.some((r) => r.toLowerCase() === typed.toLowerCase());
  const showCreate = typed !== '' && !exactMatch;
  const showPersistentCustom = typed === '' || exactMatch;

  const pickRole = (r: string): void => {
    onChange(r);
    setOpen(false);
    setCustomMode(false);
  };

  const createCustom = (): void => {
    const newRole = typed;
    if (!newRole) return;
    if (!allRoles.some((r) => r.toLowerCase() === newRole.toLowerCase())) {
      setCustomRoles((prev) => [...prev, newRole]);
    }
    onChange(newRole);
    setOpen(false);
    setCustomMode(false);
  };

  const openCustomMode = (): void => {
    onChange('');
    setCustomMode(true);
    if (inputRef.current) inputRef.current.focus();
  };

  const onInputFocus = (): void => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const onInputBlur = (): void => {
    closeTimer.current = setTimeout(() => { setOpen(false); setCustomMode(false); }, 160);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onInputFocus}
        onBlur={onInputBlur}
        placeholder={customMode ? 'Type your custom role...' : 'Pick a role or type your own'}
        style={{
          width: '100%', padding: '10px 12px', border: '0.5px solid ' + BORDER,
          borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none',
          boxSizing: 'border-box', color: TEXT_PRIMARY, background: BG_CARD,
        }}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: BG_CARD, border: '0.5px solid ' + BORDER, borderRadius: 8,
          boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
          maxHeight: 280, overflowY: 'auto', zIndex: 50,
        }}>
          {customMode && (
            <div style={{
              padding: '10px 12px', fontSize: 12.5, color: BRAND_DARK,
              borderBottom: '0.5px solid ' + BORDER_LIGHT, background: BRAND_SOFT,
            }}>
              Type your role in the field above, then choose Add "...".
            </div>
          )}
          {filtered.map((r) => {
            const isCustom = customRoles.includes(r);
            return (
              <div
                key={r}
                onMouseDown={(e) => { e.preventDefault(); pickRole(r); }}
                style={{
                  padding: '9px 12px', fontSize: 13.5, color: TEXT_PRIMARY,
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = BG_HOVER; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
              >
                <span>{r}</span>
                {isCustom && (
                  <span style={{
                    fontSize: 10, padding: '2px 6px', borderRadius: 10,
                    background: BG_HOVER, color: TEXT_TERTIARY, fontWeight: 600,
                  }}>Custom</span>
                )}
              </div>
            );
          })}
          {showCreate && (
            <div
              onMouseDown={(e) => { e.preventDefault(); createCustom(); }}
              style={{
                padding: '10px 12px', fontSize: 13.5, color: BRAND_DARK, cursor: 'pointer',
                borderTop: '0.5px solid ' + BORDER_LIGHT, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <Plus size={13} />Add "{typed}" as a custom role
            </div>
          )}
          {showPersistentCustom && (
            <div
              onMouseDown={(e) => { e.preventDefault(); openCustomMode(); }}
              style={{
                padding: '10px 12px', fontSize: 13.5, color: BRAND_DARK, cursor: 'pointer',
                borderTop: '0.5px solid ' + BORDER_LIGHT, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Plus size={13} />Custom role
              </span>
              <span style={{
                fontSize: 10, padding: '2px 6px', borderRadius: 10,
                background: BRAND_SOFT, color: BRAND_DARK, fontWeight: 600,
              }}>type your own</span>
            </div>
          )}
        </div>
      )}
      <div style={{ fontSize: 11.5, color: TEXT_TERTIARY, marginTop: 5 }}>
        Pick a role, or choose Custom role to type your own.
      </div>
    </div>
  );
}